{
  "panel": {
    "title": "Concierge",
    "assistantTitle": "Assistant Workspace",
    "subtitle": "Ask questions or describe what you need.",
    "empty": "No messages yet.",
    "emptyHint": "Tell the concierge what you're working on to get started.",
    "loading": "Loading conversation...",
    "loadFailed": "Could not load chat.",
    "uploadHint": "Upload a task description, then click <strong>Generate Plan</strong> to begin. Output template is optional — the plan structure can be derived from the task description and domain knowledge.",
    "ariaSendMessage": "Send message",
    "ariaClose": "Close",
    "feedbackPlaceholder": "Describe what should be changed or improved...",
    "verticalRequired": "No vertical configured for this workspace. Test tools require a vertical (e.g., liquidity_stress).",
    "ariaStop": "Stop generating"
  },
  "input": {
    "placeholder": "Type a message...",
    "placeholderTestRun": "Test run in progress...",
    "placeholderProcessing": "Processing...",
    "placeholderCorpusMutating": "Chat is paused while your files are being processed.",
    "placeholderNoPlan": "Chat available after plan is generated...",
    "placeholderDataReady": "Feel free to ask about data you uploaded...",
    "placeholderPlanFeedback": "Give feedback on the plan...",
    "send": "Send",
    "sending": "Sending...",
    "sendFailed": "Could not send message.",
    "attach": "Attach",
    "stop": "Stop",
    "newConversation": "New conversation",
    "uploadTaskFirstTooltip": "Upload a task description first",
    "planGenInProgressTooltip": "Plan generation in progress",
    "micStart": "Start voice input",
    "micStop": "Finish voice input",
    "micCancel": "Cancel voice input",
    "micRequestingPermission": "Allow microphone access…",
    "micListening": "Listening…",
    "micSpeechDetected": "Listening to your message…",
    "micTranscribing": "Transcribing…",
    "micDone": "Voice message sent",
    "micLevel": "Microphone level",
    "micUnsupported": "Voice input is not supported in this browser.",
    "placeholderDataReady_auditing": "Ask about the audit evidence…",
    "placeholderPlanFeedback_auditing": "Ask about the audit or share feedback…"
  },
  "message": {
    "uploadTaskFirst": "Please upload a task description to get started.",
    "you": "You",
    "concierge": "Concierge",
    "thinking": "Thinking...",
    "typing": "Concierge is typing...",
    "edited": "Edited",
    "delivered": "Delivered",
    "failed": "Failed to send",
    "retry": "Retry",
    "copy": "Copy",
    "copied": "Copied",
    "stopped": "Stopped",
    "notStoppable": "This answer can no longer be stopped.",
    "finishedBeforeStop": "This answer finished before it could be stopped.",
    "planUpdatedFallback": "Plan updated with your feedback.",
    "planUpdatedWithItems_one": "I've updated the plan with {{count}} item.",
    "planUpdatedWithItems_other": "I've updated the plan with {{count}} items.",
    "planUpdateFailedFallback": "Failed to update the plan. Please try again."
  },
  "completeness": {
    "freshness": {
      "unknown": "This earlier assessment has no recorded evidence version.",
      "superseded": "Evidence has changed since this assessment."
    },
    "title": "Data completeness",
    "checking": "Checking data...",
    "ready": "Ready to plan",
    "needsMore": "More data needed",
    "verdictReady": "Your data covers the required sections.",
    "verdictNeedsMore": "Some sections need additional data.",
    "verdictPending": "Assessing your data...",
    "viewDetails": "View details",
    "rerun": "Re-check",
    "rerunning": "Re-checking...",
    "addMoreFiles": "Add more files",
    "proceedAnyway": "Proceed anyway",
    "missingSection": "Missing data for: {{section}}",
    "loadFailed": "Could not load completeness check.",
    "lastChecked": "Last checked {{time}}",
    "dataCoverage": "Data Coverage",
    "verdicts": {
      "comprehensive": "Comprehensive",
      "adequate": "Adequate",
      "partial": "Partial",
      "insufficient": "Insufficient"
    },
    "moreGaps_one": "+{{count}} more gap",
    "moreGaps_other": "+{{count}} more gaps",
    "moreActions_one": "+{{count}} more action",
    "moreActions_other": "+{{count}} more actions",
    "moreSuggestions_one": "+{{count}} more suggestion",
    "moreSuggestions_other": "+{{count}} more suggestions",
    "anyFormat": "Any format",
    "recommendedUploads": "Recommended uploads",
    "quickActions": "Quick actions",
    "missingDocuments": "Missing documents",
    "missingData": "Missing data",
    "readyForReport": "Ready to generate the report",
    "considerMoreData": "Consider adding more data for better results",
    "moreFilesNeeded": "More files needed for reliable results"
  },
  "system": {
    "dataCompletenessFailed": "Data completeness check failed. You can proceed with report generation, but data coverage is unknown.",
    "completenessAssessmentComplete": "I've completed the data completeness assessment.",
    "dataIngestionComplete": "Your source files have been processed and indexed. The data is ready for analysis.",
    "planGenerated": "I've generated an execution plan with {{itemCount}} sections based on your task description. Please review the plan structure below.",
    "planApproved": "Plan approved! You can now upload your data source files to proceed with report generation.",
    "generationResumed": "Infrastructure halt cleared. You may now regenerate individual failed sections, or re-run section generation for the remaining sections via the main Generate button.",
    "planFeedbackProgress": "Working on your plan feedback — revising sections..."
  },
  "history": {
    "today": "Today",
    "yesterday": "Yesterday",
    "older": "Older"
  },
  "senderLabel": {
    "you": "You",
    "bennie": "Bennie",
    "bennieDataSearch": "Bennie (Data Search)"
  },
  "statusIndicators": {
    "reconnecting": "Connection interrupted — checking whether your answer completed...",
    "regeneratingSection": "Rewriting section {{sectionId}} — this usually takes a few minutes.",
    "regeneratingSectionUnnamed": "Rewriting the section — this usually takes a few minutes.",
    "regeneratingElapsed": "{{minutes}} min elapsed",
    "querying": "Querying knowledge base...",
    "connecting": "Connecting the dots...",
    "crunching": "Crunching the details...",
    "phrases": [
      "Thinking...",
      "Processing your request...",
      "Working on it...",
      "Reviewing context...",
      "Connecting the dots...",
      "Synthesizing information...",
      "Formulating a response...",
      "Analyzing your input...",
      "Building a response...",
      "Crunching the details...",
      "Cross-referencing sources...",
      "Assembling insights...",
      "Putting thoughts together...",
      "Weighing options...",
      "Digging deeper...",
      "Almost there...",
      "Bear with me...",
      "Good question...",
      "Searching your documents...",
      "Querying knowledge base...",
      "Extracting relevant data...",
      "Matching data requirements...",
      "Evaluating plan structure...",
      "Checking regulatory alignment...",
      "One moment...",
      "This one is interesting...",
      "Lots to consider here...",
      "Reviewing scope implications...",
      "Mapping document flow...",
      "Scanning source files..."
    ]
  },
  "refusal": {
    "planAlreadyApproved": {
      "report": "The report plan has been approved and is locked, so it can no longer be changed.",
      "de_checklist": "The design-effective checklist has been approved and is locked, so it can no longer be changed.",
      "oe_checklist": "The operating-effective checklist has been approved and is locked, so it can no longer be changed."
    },
    "planRevisionIntegrityFailed": "The revision failed the plan integrity checks. Your plan was unchanged. Please clarify the changes you want.",
    "planRevisionSavedResponseFailed": "Your revision was saved, but its confirmation failed. Review the saved plan before requesting another change.",
    "runItemRegenDisabled": "Design- and operating-effective results can't be regenerated one item at a time. To change them, update the evidence and run the whole phase again."
  }
}
